import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import BudgetType from '../../../enums/budget-type';
import { FarmUser } from '../../../interfaces/farm-user';
import { can } from '../../../services/utils';
import { useService } from '../../../services/services';
import Accordion, {
  AccordionItem,
  PlainAccordionItemBody,
  PlainAccordionItemHeader,
} from '../../../components/common/accordion';
import { ButtonComponent } from '../../../components/form/elements/button';
import TableHeadings from './components/table-headings';
import TableRow from './components/table-row';
import DashboardLoading from '../../dashboard/components/dashboard-loading';
import { budgetBlueprint, IBudget, IBudgetItem } from './blueprint';
import css from './index.module.css';

interface IValues {
  [key: string]: number;
}

export default function BudgetSummary() {
  const { budgetService } = useService();
  const { farmId, season } = useParams<{ farmId: string; season: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [initialData, setInitialData] = useState(null);
  const [defaultValues, setDefaultValues] = useState(null);

  const fillData = useCallback(
    (defValues: IValues, data: IBudget, response: any) => {
      const dataFixes = {
        fixTotal: (name: string) => {
          const item = response.find((item: IBudgetItem) => item.name === name);

          if (!item && data[name]?.calculable?.length) {
            const values: number[] = [];
            data[name].calculable.forEach((fieldName: string) => {
              const field = response.find(
                (item: IBudgetItem) => item.name === fieldName
              );
              values.push(parseFloat(field?.value) || 0);
            });

            if (values.length) {
              const sum = values.reduce((accumulator, currentValue) => {
                if (
                  typeof currentValue === 'number' ||
                  typeof currentValue === 'string'
                ) {
                  return parseFloat(
                    (accumulator + Number(currentValue)).toFixed(2)
                  );
                }
                return accumulator;
              }, 0);

              data[name].value = sum;
              defValues[name] = sum;
            }
          }
        },
      };

      response.forEach((item: any) => {
        defValues[item.name] = +item.value;

        const parentName = getBudgetParentName(item.name);

        if (data[parentName] && data[parentName].children) {
          data[parentName].children[item.name].value = +item.value;
          data[parentName].children[item.name].id = item.id;
        } else {
          if (!data[item.name]) return;

          data[item.name].value = +item.value;
          data[item.name].id = item.id;
        }
      });

      dataFixes.fixTotal('revenues_total');
      dataFixes.fixTotal('costs_total');
    },
    []
  );

  useEffect(() => {
    setIsLoading(true);

    const subscription = budgetService
      .index(farmId, season)
      .subscribe((response) => {
        let data: IBudget = JSON.parse(JSON.stringify(budgetBlueprint));
        let defaultValues: IValues = {};

        Object.keys(budgetBlueprint).forEach((key) => {
          defaultValues[key] = budgetBlueprint[key].value;
          if (budgetBlueprint[key].children) {
            Object.keys(budgetBlueprint[key].children).forEach((childKey) => {
              defaultValues[childKey] =
                budgetBlueprint[key].children[childKey].value;
            });
          }
        });

        Object.keys(data).forEach((key) => {
          if (
            key === BudgetType.REVENUES_CROP ||
            key === BudgetType.REVENUES_ANIMAL
          ) {
            delete data[key].children;
            delete data[key].calculable;
            data[key].disabled = true;
          }
          if (
            key === BudgetType.COSTS_CROP ||
            key === BudgetType.COSTS_ANIMAL
          ) {
            data[key].disabled = true;
            Object.keys(data[key].children).forEach((childKey: string) => {
              data[key].children[childKey].disabled = true;
            });
          }
        });

        if (Array.isArray(response) && response.length) {
          fillData(defaultValues, data, response);
        }

        setInitialData(data);
        setDefaultValues(defaultValues);
        setIsLoading(false);
      });

    return () => subscription.unsubscribe();
  }, [farmId, season, budgetService, fillData]);

  return isLoading ? (
    <DashboardLoading />
  ) : (
    <BudgetSummaryTable
      initialData={initialData}
      defaultValues={defaultValues}
    />
  );
}

interface Props {
  initialData: IBudget;
  defaultValues: IValues;
}

export function BudgetSummaryTable({ initialData, defaultValues }: Props) {
  const { t } = useTranslation();
  const { budgetService } = useService();
  const { farmId, season } = useParams<{ farmId: string; season: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const formMethods = useForm({
    resolver: null,
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
  });
  const { handleSubmit } = formMethods;
  const fillIds = useCallback(
    (response) => {
      response.forEach((item: IBudgetItem) => {
        const parentName = getBudgetParentName(item.name);
        if (initialData[parentName] && initialData[parentName].children) {
          initialData[parentName].children[item.name].id = item.id;
        } else {
          if (initialData[item.name]) {
            initialData[item.name].id = item.id;
          }
        }
      });
    },
    [initialData]
  );

  const { farmUser } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
  }));

  const onSubmit = (formData: any) => {
    if (allowedToStore(farmUser)) {
      setIsLoading(true);
      setError(null);

      const submitData: any = {};
      Object.keys(initialData).forEach((key: string) => {
        if (initialData[key].children) {
          Object.keys(initialData[key].children).forEach((childKey: string) => {
            submitData[initialData[key].children[childKey].name] = JSON.parse(
              JSON.stringify(initialData[key].children[childKey])
            );
            submitData[initialData[key].children[childKey].name].value =
              formData[childKey];
          });
        }
        submitData[initialData[key].name] = JSON.parse(
          JSON.stringify(initialData[key])
        );
        submitData[initialData[key].name].value = formData[key];
      });

      const submitDataArray: any = { budgets: [] };
      Object.keys(submitData).forEach((key: string) => {
        submitDataArray.budgets.push(submitData[key]);
      });

      if (submitDataArray.budgets[0].id) {
        budgetService.store(farmId, season, submitDataArray).subscribe(
          (response) => {
            fillIds(response);
            setIsLoading(false);
          },
          () => {
            setIsLoading(false);
            setError(t('pages.finance.budget.error'));
          }
        );
      } else {
        budgetService.store(farmId, season, submitDataArray).subscribe(
          (response) => {
            fillIds(response);
            setIsLoading(false);
          },
          () => {
            setIsLoading(false);
            setError(t('pages.finance.budget.error'));
          }
        );
      }
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form className={css['table']} onSubmit={handleSubmit(onSubmit)}>
        <TableHeadings />

        {Object.keys(initialData).map((key: string) => {
          return initialData[key].children ? (
            <Accordion key={`row-${initialData[key].name}}`}>
              <AccordionItem
                header={
                  <PlainAccordionItemHeader>
                    <TableRow
                      key={`row-body-${initialData[key].name}`}
                      item={initialData[key]}
                      dataName={initialData[key].name + '_accordion'}
                    />
                  </PlainAccordionItemHeader>
                }
                body={
                  <PlainAccordionItemBody>
                    {Object.keys(initialData[key].children).map(
                      (childKey: string) => (
                        <TableRow
                          key={`row-body-${initialData[key].children[childKey].name}`}
                          item={initialData[key].children[childKey]}
                        />
                      )
                    )}
                  </PlainAccordionItemBody>
                }
                opened={false}
              />
            </Accordion>
          ) : (
            <TableRow
              key={`row-${initialData[key].name}`}
              item={initialData[key]}
            />
          );
        })}

        <div className={css['error']}>{error}</div>

        {allowedToStore(farmUser) && (
          <ButtonComponent
            name={'submit'}
            click={null}
            label={t('pages.finance.budget.save')}
            disabled={isLoading}
          />
        )}
      </form>
    </FormProvider>
  );
}

function allowedToStore(farmUser: FarmUser) {
  return (
    can(farmUser, { title: 'create budget' }) &&
    can(farmUser, { title: 'update budget' })
  );
}

function getBudgetParentName(itemName: string): string {
  const parts = itemName.split('_');
  parts.pop();
  return parts.join('_');
}
